import { useState, useRef } from 'react';
import { Popup } from 'antd-mobile';
import { Camera } from 'lucide-react';
import { useStore } from '../store';
import { api } from '../api';
import './ShopNameModal.css';

interface ShopNameModalProps {
    visible: boolean;
    onClose: () => void;
}

export default function ShopNameModal({ visible, onClose }: ShopNameModalProps) {
    const dbUser = useStore((s) => s.dbUser);
    const setDbUser = useStore((s) => s.setDbUser);
    const [shopName, setShopName] = useState('');
    const [shopImageFile, setShopImageFile] = useState<File | null>(null);
    const [shopImagePreview, setShopImagePreview] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setShopImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setShopImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        if (!shopName.trim() || !dbUser) return;

        setSaving(true);
        try {
            let imageUrl: string | null = null;

            // 1. Upload image if selected
            if (shopImageFile) {
                const uploadRes = await api.uploadImage(shopImageFile);
                if (uploadRes.success) {
                    imageUrl = uploadRes.url;
                }
            }

            // 2. Update user profile
            const updateData: { shop_name: string; shop_image?: string } = {
                shop_name: shopName.trim(),
            };
            if (imageUrl) {
                updateData.shop_image = imageUrl;
            }

            const result = await api.updateUser(dbUser.line_user_id, updateData);
            if (result.data) {
                setDbUser(result.data);
                onClose();
            }
        } catch (error) {
            console.error('Error saving shop info:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popup
            visible={visible}
            onMaskClick={() => {}}
            bodyStyle={{
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                minHeight: '500px',
            }}
        >
            <div className="shop-modal">
                <div className="shop-modal-header">
                    <div className="shop-modal-title">ข้อมูลร้านค้า</div>
                    <button onClick={onClose} className="shop-modal-close">X</button>
                </div>

                {/* Image Upload */}
                <div className="shop-image-upload-area">
                    <div
                        className="shop-image-preview"
                        onClick={() => fileInputRef.current?.click()}
                        style={shopImagePreview ? { backgroundImage: `url(${shopImagePreview})` } : {}}
                    >
                        {!shopImagePreview && (
                            <div className="upload-placeholder">
                                <Camera size={40} color="#ccc" />
                                <span>แตะเพื่อเพิ่มรูปร้าน</span>
                            </div>
                        )}
                        {shopImagePreview && (
                            <div className="edit-overlay">
                                <Camera size={20} color="#fff" />
                            </div>
                        )}
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                    <div className="upload-hint">แนะนำรูปแนวนอน (Cover) จะสวยที่สุด</div>
                </div>

                {/* Shop Name Input */}
                <div className="shop-form-group">
                    <label className="shop-form-label">ชื่อร้าน</label>
                    <input
                        type="text"
                        className="shop-form-input"
                        placeholder="เช่น ร้านกาแฟรักคุณ"
                        value={shopName}
                        onChange={(e) => setShopName(e.target.value)}
                        maxLength={50}
                    />
                </div>

                {/* Save Button */}
                <div className="shop-form-actions">
                    <button
                        className="shop-save-btn"
                        onClick={handleSave}
                        disabled={saving || !shopName.trim()}
                    >
                        {saving ? 'กำลังอัปโหลด...' : 'บันทึกข้อมูล'}
                    </button>
                </div>
            </div>
        </Popup>
    );
}
